<template>
  <button
      :type="type"
      :disabled="disabled"
      :class="buttonClasses"
      @click="handleClick"
  >
    <span v-if="$slots.icon" class="button-icon">
      <slot name="icon"></slot>
    </span>
    <span class="button-content">
      <template v-if="name">{{name}}</template>
      <slot v-else></slot>
    </span>
  </button>
</template>

<script setup>
import { computed } from 'vue'

// Props
const props = defineProps({
  variant: {
    type: String,
    default: 'default'
  },
  size: {
    type: String,
    default: 'medium',
    validator: (value) => ['small', 'medium', 'large'].includes(value)
  },
  type: {
    type: String,
    default: 'button'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  block: {
    type: Boolean,
    default: false
  },
  name: {
    type: String,
    default: ''
  }
})

// Emits
const emit = defineEmits(['click'])

// Computed
const buttonClasses = computed(() => {
  return [
    'styled-button',
    `styled-button--${props.variant}`,
    `styled-button--${props.size}`,
    {
      'styled-button--disabled': props.disabled,
      'styled-button--block': props.block
    }
  ]
})

// Methods
const handleClick = (event) => {
  if (!props.disabled) {
    emit('click', event)
  }
}
</script>

<style lang="scss" scoped>
@use '../../scss/variables' as *;

.styled-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: $spacing-2;
  border-radius: $btn-border-radius;
  font-family: $font-family-base;
  font-weight: $font-weight-medium;
  cursor: pointer;
  transition: all $transition-fast $transition-timing;
  text-decoration: none;
  border: 1px solid transparent;
  box-sizing: border-box;
  white-space: nowrap;
  outline: none;
  user-select: none;

  &:focus-visible {
    box-shadow: 0 0 0 $focus-ring-width $focus-ring-color;
  }

  &:active:not(.styled-button--disabled) {
    transform: scale(0.98);
  }
}

/* Sizes */
.styled-button--small {
  padding: $btn-padding-sm;
  font-size: $btn-font-size-sm;
  height: $btn-height-sm;
}

.styled-button--medium {
  padding: $btn-padding-md;
  font-size: $btn-font-size-md;
  height: $btn-height-md;
}

.styled-button--large {
  padding: $btn-padding-lg;
  font-size: $btn-font-size-lg;
  height: $btn-height-lg;
}

/* ========================================
   Variants - Modern Design System
   ======================================== */

/* Default - Outlined gray style */
.styled-button--default {
  background-color: $color-bg-primary;
  border-color: $color-border-default;
  color: $color-text-primary;

  &:hover:not(.styled-button--disabled) {
    background-color: $color-gray-50;
    border-color: $color-border-dark;
  }
}

/* Primary - Main action button */
.styled-button--primary,
.styled-button--filled {
  background-color: $color-primary;
  border-color: $color-primary;
  color: $color-text-inverse;

  &:hover:not(.styled-button--disabled) {
    background-color: $color-primary-dark;
    border-color: $color-primary-dark;
    box-shadow: $shadow-primary;
  }
}

/* Secondary - Less prominent action */
.styled-button--secondary {
  background-color: $color-gray-600;
  border-color: $color-gray-600;
  color: $color-text-inverse;

  &:hover:not(.styled-button--disabled) {
    background-color: $color-gray-700;
    border-color: $color-gray-700;
  }
}

/* Third - Dark emphasis button */
.styled-button--third {
  background-color: $color-gray-800;
  border-color: $color-gray-800;
  color: $color-text-inverse;

  &:hover:not(.styled-button--disabled) {
    background-color: $color-gray-900;
    border-color: $color-gray-900;
  }
}

/* Outline - Bordered button */
.styled-button--outline {
  background-color: transparent;
  border-color: $color-primary;
  color: $color-primary;

  &:hover:not(.styled-button--disabled) {
    background-color: $color-primary-50;
  }
}

/* Text - Minimal button */
.styled-button--text {
  background-color: transparent;
  border-color: transparent;
  color: $color-primary;
  padding-left: $spacing-2;
  padding-right: $spacing-2;

  &:hover:not(.styled-button--disabled) {
    background-color: $color-primary-50;
  }
}

/* Success */
.styled-button--success {
  background-color: $color-success;
  border-color: $color-success;
  color: $color-text-inverse;

  &:hover:not(.styled-button--disabled) {
    background-color: $color-success-dark;
    border-color: $color-success-dark;
    box-shadow: $shadow-success;
  }
}

/* Danger */
.styled-button--danger {
  background-color: $color-danger;
  border-color: $color-danger;
  color: $color-text-inverse;

  &:hover:not(.styled-button--disabled) {
    background-color: $color-danger-dark;
    border-color: $color-danger-dark;
    box-shadow: $shadow-danger;
  }
}

/* Warning */
.styled-button--warning {
  background-color: $color-warning;
  border-color: $color-warning;
  color: $color-text-inverse;

  &:hover:not(.styled-button--disabled) {
    background-color: $color-warning-dark;
    border-color: $color-warning-dark;
  }
}

/* Info */
.styled-button--info {
  background-color: $color-info;
  border-color: $color-info;
  color: $color-text-inverse;

  &:hover:not(.styled-button--disabled) {
    background-color: $color-info-dark;
    border-color: $color-info-dark;
  }
}

/* ========================================
   States
   ======================================== */

.styled-button--disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.styled-button--block {
  width: 100%;
}

/* Icon styling */
.button-icon {
  display: flex;
  align-items: center;
  line-height: 0;
}

.button-content {
  display: flex;
  align-items: center;
}
</style>
